import { DnsRecord } from '../../../types/domains.ts';
import { Copy, CheckCircle, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';

type ItemType = DnsRecord;
const DnsTable = ({items}: {items: ItemType[]}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (value: string | string[], type: string, index: number) => {
    const valueToClip = Array.isArray(value) ? value.join(', ') : value;
    navigator.clipboard.writeText(valueToClip);
    setCopiedIndex(index);
    toast.success(`Copied ${type} record value to clipboard`);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCopyHost = (value: string) => {
    toast.success(`Copied ${value} to clipboard`);
    navigator.clipboard.writeText(value);
  }
  if (!items || items.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        <p>No DNS records available</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className='grid grid-cols-12 gap-4 p-4 bg-gray-50 dark:bg-gray-900 text-sm font-semibold text-gray-700 dark:text-gray-300'> 
        <div className='col-span-2'>Type</div>
        <div className='col-span-3'>Host</div>
        <div className='col-span-6'>Value</div>
        <div className='col-span-1'>Action</div>
      </div>

      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {items.map((record, index) => (
          <div key={index} className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <div className="col-span-2 flex items-center">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-2 ${
                record.isVerified 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : record.type === 'CNAME' 
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                : record.type === 'A'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
              }`}>
                {record.isVerified && <BadgeCheck className="size-4 text-green-500" />} 
                {record.type}
              </span>
            </div>
            
            <div className="col-span-3 flex items-center">
              <code onClick={() => handleCopyHost(record.name)} className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm font-mono overflow-x-auto text-nowrap scrollbar-hide cursor-pointer">
                {record.name}
              </code>
            </div>
            
            <div className="col-span-6 flex items-center">
              <div className="bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg font-mono text-sm w-full break-all overflow-x-auto text-nowrap scrollbar-hide">
                {Array.isArray(record.value) ? record.value.join(', ') : record.value}
              </div>
            </div>
            
            <div className="col-span-1 flex items-center">
              <Button
                onClick={() => handleCopy(record.value, record.type, index)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  copiedIndex === index
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                title={`Copy ${record.type} record`}
              >
                {copiedIndex === index ? (
                  <CheckCircle className="size-4" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default DnsTable